import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Bell,
  Phone,
  Calendar,
  Users,
  Clock,
  Check,
  Trash2,
  MoreVertical,
  AlertCircle,
  Search,
  Filter,
  Loader2,
} from 'lucide-react';
import api from '../services/api';

const REMINDER_TYPES = {
  follow_up: { label: 'Follow-up', icon: Calendar, color: 'bg-blue-100 text-blue-600' },
  call: { label: 'Call', icon: Phone, color: 'bg-green-100 text-green-600' },
  meeting: { label: 'Meeting', icon: Users, color: 'bg-purple-100 text-purple-600' },
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'snoozed', label: 'Snoozed' },
  { value: 'completed', label: 'Completed' },
];

const TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'call', label: 'Call' },
  { value: 'meeting', label: 'Meeting' },
];

const SNOOZE_OPTIONS = [
  { label: '15 minutes', value: 15 },
  { label: '30 minutes', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '1 day', value: 1440 },
];

const RemindersPage = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', type: 'all', search: '' });
  const [stats, setStats] = useState({ pending: 0, overdue: 0, completed: 0 });

  const fetchReminders = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.type !== 'all') params.append('type', filters.type);
      
      const response = await api.get(`/reminders?${params.toString()}`);
      let data = response.data;
      
      // Client-side search filter
      if (filters.search) {
        const search = filters.search.toLowerCase();
        data = data.filter(r =>
          r.title?.toLowerCase().includes(search) ||
          r.leadName?.toLowerCase().includes(search) ||
          r.description?.toLowerCase().includes(search)
        );
      }
      
      setReminders(data);
      
      // Calculate stats
      const now = new Date();
      const pendingCount = data.filter(r => r.status === 'pending' || r.status === 'snoozed').length;
      const overdueCount = data.filter(r => 
        (r.status === 'pending' || r.status === 'snoozed') && 
        new Date(r.scheduledAt) < now
      ).length;
      const completedCount = data.filter(r => r.status === 'completed').length;
      
      setStats({ pending: pendingCount, overdue: overdueCount, completed: completedCount });
    } catch (error) {
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.type]);

  const handleComplete = async (reminderId) => {
    try {
      await api.post(`/reminders/${reminderId}/complete`);
      toast.success('Reminder marked as complete');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const handleSnooze = async (reminderId, minutes) => {
    try {
      await api.post(`/reminders/${reminderId}/snooze?minutes=${minutes}`);
      toast.success(`Reminder snoozed for ${minutes >= 60 ? `${minutes / 60} hour(s)` : `${minutes} minutes`}`);
      fetchReminders();
    } catch (error) {
      toast.error('Failed to snooze reminder');
    }
  };

  const handleDelete = async (reminderId) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) return;
    
    try {
      await api.delete(`/reminders/${reminderId}`);
      toast.success('Reminder deleted');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const isOverdue = (reminder) => {
    return (reminder.status === 'pending' || reminder.status === 'snoozed') && 
           new Date(reminder.scheduledAt) < new Date();
  };

  const getStatusBadge = (reminder) => {
    if (reminder.status === 'completed') {
      return <Badge variant="outline" className="bg-green-50 text-green-600">Completed</Badge>;
    }
    if (isOverdue(reminder)) {
      return <Badge variant="destructive">Overdue</Badge>;
    }
    if (reminder.status === 'snoozed') {
      return <Badge variant="secondary">Snoozed</Badge>;
    }
    return <Badge variant="outline">Pending</Badge>;
  };

  const filteredReminders = filters.search
    ? reminders.filter(r =>
        r.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
        r.leadName?.toLowerCase().includes(filters.search.toLowerCase())
      )
    : reminders;

  return (
    <div className="space-y-6" data-testid="reminders-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6 text-primary" />
            Reminders
          </h1>
          <p className="text-muted-foreground">Manage your follow-ups, calls, and meetings</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.overdue}</p>
                <p className="text-sm text-muted-foreground">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search reminders..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="reminder-search-input"
              />
            </div>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.type}
              onValueChange={(value) => setFilters({ ...filters, type: value })}
            >
              <SelectTrigger className="w-full sm:w-40" data-testid="type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reminders Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredReminders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
              <Bell className="h-10 w-10 mb-2 opacity-50" />
              <p>No reminders found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReminders.map((reminder) => {
                  const typeInfo = REMINDER_TYPES[reminder.type] || REMINDER_TYPES.follow_up;
                  const TypeIcon = typeInfo.icon;
                  
                  return (
                    <TableRow key={reminder.id} data-testid={`reminder-row-${reminder.id}`}>
                      <TableCell>
                        <div className={`p-1.5 rounded-full ${typeInfo.color} w-fit`}>
                          <TypeIcon className="h-4 w-4" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{reminder.title}</p>
                          {reminder.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {reminder.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{reminder.leadName || 'Unknown'}</span>
                      </TableCell>
                      <TableCell>
                        <span className={`text-sm ${isOverdue(reminder) ? 'text-red-500' : ''}`}>
                          {formatDateTime(reminder.scheduledAt)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(reminder)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {reminder.status !== 'completed' && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleComplete(reminder.id)}
                                title="Mark as done"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <Clock className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {SNOOZE_OPTIONS.map((opt) => (
                                    <DropdownMenuItem
                                      key={opt.value}
                                      onClick={() => handleSnooze(reminder.id, opt.value)}
                                    >
                                      Snooze {opt.label}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => window.open(`/leads-board?leadId=${reminder.leadId}`, '_self')}
                              >
                                View Lead
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(reminder.id)}
                                className="text-destructive"
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RemindersPage;
